import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n/I18nProvider";
import LanguageToggle from "@/components/LanguageToggle";

const NAV_KEYS = [
  { to: "/", label: "nav.home", testid: "nav-home" },
  { to: "/for-students", label: "nav.forStudents", testid: "nav-students" },
  { to: "/for-business", label: "nav.forBusiness", testid: "nav-business" },
  { to: "/pricing", label: "nav.pricing", testid: "nav-pricing" },
  { to: "/audit", label: "nav.audit", testid: "nav-audit" },
  { to: "/about", label: "nav.about", testid: "nav-about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useI18n();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        "sticky top-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-brand-off/85 backdrop-blur-md border-b border-brand-line"
          : "bg-transparent border-b border-transparent",
      )}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20 gap-4">
        <Link
          to="/"
          data-testid="nav-logo"
          className="group flex items-center gap-2 shrink-0"
        >
          <span className="inline-block w-2 h-2 rounded-full bg-brand-charcoal group-hover:bg-brand-blue transition-colors" />
          <span className="font-semibold tracking-tight text-brand-charcoal text-[15px]">
            Toms Clarifies
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5">
          {NAV_KEYS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              className={({ isActive }) =>
                cn(
                  "px-3 py-2 text-[13px] tracking-tight rounded-full transition-colors whitespace-nowrap",
                  isActive
                    ? "text-brand-charcoal bg-brand-line/70"
                    : "text-brand-muted hover:text-brand-charcoal",
                )
              }
            >
              {t(item.label)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <LanguageToggle />
          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="hidden lg:inline-flex group items-center gap-1.5 rounded-full bg-brand-charcoal text-brand-off px-4 py-2 text-[13px] font-medium hover:bg-brand-dark transition-colors"
          >
            {t("nav.cta")}
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>

          <button
            type="button"
            aria-label={t("nav.menu")}
            data-testid="mobile-menu-toggle"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-brand-line text-brand-charcoal"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-brand-line bg-brand-off/95 backdrop-blur"
        >
          <div className="container-x py-4 flex flex-col gap-1">
            {NAV_KEYS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`m-${item.testid}`}
                className={({ isActive }) =>
                  cn(
                    "px-3 py-3 text-[15px] rounded-xl",
                    isActive
                      ? "bg-brand-line/70 text-brand-charcoal"
                      : "text-brand-charcoal/80",
                  )
                }
              >
                {t(item.label)}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="m-nav-cta-contact"
              className="mt-2 inline-flex items-center justify-between gap-2 rounded-xl bg-brand-charcoal text-brand-off px-4 py-3 text-[14px] font-medium"
            >
              {t("nav.cta")}
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
